import { createApi } from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../../config/apiConfig";

/**
 * بقى مفيش أي توكن ولا مفتاح مطلوب خالص من الفرونت اند.
 * الباك اند بقى بيسجل كل الطلبات الجايه على connector/api/* تلقائياً
 * بحساب الموقع الثابت (شوف middleware: ConnectorAutoAuth).
 *
 * ================================================================
 * ملحوظة مهمة عن الـ CORS Preflight (OPTIONS):
 * ================================================================
 * السيرفر (LiteSpeed على Hostinger) بيرجع خطأ غريب على أي طلب
 * OPTIONS بيوصله (الطلب اللي المتصفح بيبعته تلقائياً قبل أي POST
 * فيه Content-Type: application/json)، وده بيوقف تسجيل الدخول
 * والتسجيل بالكامل بسبب مشكلة في إعدادات السيرفر مش في الكود.
 *
 * عشان نتفادى المشكلة دي *بالكامل* من غير ما نغيّر أي حاجة في
 * السيرفر أو في التوكن/الـ middleware، بنبعت كل الطلبات بصيغة
 * application/x-www-form-urlencoded بدل json. الصيغة دي معتبرة
 * "طلب بسيط" (Simple Request) حسب مواصفات CORS، فالمتصفح مبيبعتش
 * OPTIONS خالص قبلها - بيروح على طول للـ POST.
 *
 * الباك اند (Laravel) بيقرأ القيم بـ $request->input() / ->all()
 * وده بيشتغل تمام مع أي صيغة (json أو urlencoded) من غير أي تعديل
 * مطلوب في السيرفر.
 * ================================================================
 */

// يحول أي object (حتى المتداخل) لصيغة x-www-form-urlencoded
function toUrlEncoded(body) {
  const params = new URLSearchParams();
  if (body && typeof body === "object") {
    Object.entries(body).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === "object") {
        // أي object/array متداخل بنحوله لـ JSON string كقيمة واحدة
        params.append(key, JSON.stringify(value));
      } else {
        params.append(key, value);
      }
    });
  }
  return params;
}

/**
 * baseQuery مخصص بيستخدم fetch مباشرة (بدل fetchBaseQuery الجاهزة)
 * عشان نضمن 100% إن الطلب هيبقى "بسيط" ومايحتاجش Preflight.
 */
const baseQuery = async (args) => {
  const isFullQuery = typeof args === "object" && args !== null;
  const url = isFullQuery ? args.url : args;
  const method = isFullQuery ? args.method || "GET" : "GET";
  const body = isFullQuery ? args.body : undefined;

  const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;

  const fetchOptions = {
    method,
    headers: {
      // مفيش Content-Type بنحطه هنا يدوي عشان المتصفح هو اللي
      // يحدد القيمة الآمنة (application/x-www-form-urlencoded)
      // تلقائياً لما نبعت URLSearchParams كـ body.
      Accept: "application/json",
    },
  };

  if (method !== "GET" && body !== undefined) {
    fetchOptions.body = toUrlEncoded(body);
  }

  try {
    const response = await fetch(fullUrl, fetchOptions);
    const contentType = response.headers.get("content-type") || "";
    const data = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data,
        },
      };
    }
    return { data };
  } catch (error) {
    return {
      error: {
        status: "FETCH_ERROR",
        error: String(error?.message || error),
      },
    };
  }
};

/**
 * إعداد API Slice
 */
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: ["Product", "Products", "Token", "Categories"],

  endpoints: (builder) => ({
    // ==================== المستخدم ====================
    login: builder.mutation({
      query: (credentials) => ({
        url: "connector/api/user/login",
        method: "POST",
        body: credentials,
      }),
    }),
    register: builder.mutation({
      query: (newUser) => ({
        url: "connector/api/user-registration",
        method: "POST",
        body: newUser,
      }),
    }),
    sendResetOtp: builder.mutation({
      query: (data) => ({
        url: "connector/api/forgot-password/send-otp",
        method: "POST",
        body: data,
      }),
    }),
    verifyResetOtp: builder.mutation({
      query: (data) => ({
        url: "connector/api/forgot-password/verify-otp",
        method: "POST",
        body: data,
      }),
    }),
    resetPasswordWithOtp: builder.mutation({
      query: (data) => ({
        url: "connector/api/forgot-password/reset-password",
        method: "POST",
        body: data,
      }),
    }),
    getSiteTheme: builder.query({
      query: () => ({
        url: "connector/api/site-theme",
        method: "GET",
      }),
    }),
    // sliders
    GetSlider: builder.mutation({
      query: () => ({
        url: "connector/api/slide",
        method: "POST",
        body: {},
      }),
    }),

    // جلب جميع المنتجات
    getAllProducts: builder.query({
      query: ({ page = 1, per_page = 50 } = {}) =>
        `connector/api/product?page=${page}&per_page=${per_page}`,
      providesTags: ["Product"],
    }),

    //  جلب جميع المنتجات المميزه
    getFeatureProducts: builder.query({
      query: (page = 1) => `connector/api/feature_product?page=${page}`,
    }),

    // بحث عن المنتجات
    searchProducts: builder.query({
      query: (searchTerm) =>
        `connector/api/product/?query=${encodeURIComponent(searchTerm)}`,
    }),
    // جلب جميع المناطق
    getGovernorate: builder.query({
      query: () => "connector/api/governorate",
    }),
    // جلب العروض الفعّالة حاليًا (خصم عند شراء كمية محددة من منتج)
    getOffers: builder.query({
      query: () => "connector/api/offers",
    }),
    // جلب رصيد نقاط المكافآت الخاصة بالعميل
    getRewardPoints: builder.query({
      query: (arg) => {
        const contactId = typeof arg === "object" ? arg.contactId : arg;
        const orderTotal = typeof arg === "object" ? arg.orderTotal : undefined;
        return `connector/api/reward-points/${contactId}${orderTotal ? `?order_total=${orderTotal}` : ""}`;
      },
    }),
    // انشاء اوردر من عربة التسوق
    CreateOrderFromCart: builder.mutation({
      query: (cartData) => ({
        url: "connector/api/neworder",
        method: "POST",
        body: cartData,
      }),
    }),

    // ==================== الطلبات ====================
    getOrdersByContactId: builder.mutation({
      query: ({ contactId, page = 1, per_page = 50 }) => ({
        url: `connector/api/getorders/${contactId}`,
        method: "POST",
        body: { page, per_page },
      }),
    }),
    getOrderById: builder.mutation({
      query: (orderId) => ({
        url: `connector/api/getorder/${orderId}`,
        method: "POST",
        body: {},
      }),
    }),
    // جلب منتج واحد حسب ID
    getOneProduct: builder.query({
      query: (id) => `connector/api/product/${id}`,
      providesTags: (result, error, id) => [{ type: "Products", id }],
    }),

    // ==================== التصنيفات ====================
    getCategories: builder.query({
      query: () => "connector/api/categories",
      providesTags: ["Categories"],
    }),

    getCategorieById: builder.query({
      query: ({ id, per_page = 500 } = {}) =>
        `connector/api/categories/${id}?per_page=${per_page}`,
      providesTags: ["Categories"],
    }),

    getCategorieByIdPaged: builder.query({
      query: ({ id, page = 1, per_page = 50 } = {}) =>
        `connector/api/categories/${id}?page=${page}&per_page=${per_page}`,
      providesTags: ["Categories"],
    }),

    // ==================== علامه تجاريه ====================
    getBrands: builder.query({
      query: () => "connector/api/brand",
      providesTags: ["Brands"],
    }),

    getBrandById: builder.query({
      query: (id) => `connector/api/brand/${id}`,
      providesTags: ["Brands"],
    }),

    // ==================== تغيير كلمة المرور ====================
    updatePassword: builder.mutation({
      query: (credentials) => ({
        url: "connector/api/update-password",
        method: "POST",
        body: credentials,
      }),
    }),

    // ==================== تحديث العنوان ====================
    updateAddress: builder.mutation({
      query: (data) => ({
        url: "connector/api/update-address",
        method: "POST",
        body: data,
      }),
    }),

    // ==================== الشريط المتحرك ====================
    getTicker: builder.query({
      query: () => "connector/api/ticker",
    }),

    // ==================== مواعيد العمل ====================
    getWorkHours: builder.query({
      query: () => "connector/api/work-hours",
      keepUnusedDataFor: 60,
    }),

    // ==================== ترتيب أقسام الصفحة الرئيسية ====================
    getHomeSections: builder.query({
      query: () => "connector/api/home-sections",
      keepUnusedDataFor: 120,
    }),

    // ==================== الاجازات ====================
    Vacations: builder.mutation({
      query: () => ({
        url: "connector/api/vacations",
        method: "POST",
      }),
      invalidatesTags: ["Vacations"],
    }),
  }),
});

// استخراج الـ hooks
export const {
  useLoginMutation,
  useRegisterMutation,
  useSendResetOtpMutation,
  useVerifyResetOtpMutation,
  useResetPasswordWithOtpMutation,
  useGetSiteThemeQuery,
  useGetSliderMutation,
  useGetAllProductsQuery,
  useSearchProductsQuery,
  useGetFeatureProductsQuery,
  useGetCategoriesQuery,
  useGetCategorieByIdQuery,
  useGetCategorieByIdPagedQuery,
  useGetBrandsQuery,
  useGetBrandByIdQuery,
  useGetOneProductQuery,
  useGetGovernorateQuery,
  useGetOffersQuery,
  useGetRewardPointsQuery,
  useCreateOrderFromCartMutation,
  useGetOrderByIdMutation,
  useGetOrdersByContactIdMutation,
  useVacationsMutation,
  useUpdatePasswordMutation,
  useUpdateAddressMutation,
  useGetTickerQuery,
  useGetWorkHoursQuery,
  useGetHomeSectionsQuery,
} = apiSlice;

// خلاص مبقاش فيه توكن نتأكد منه وقت فتح الموقع، فالدالة دي بقت no-op
// (سايبينها موجودة عشان مانعملش تعديل في index.tsx)
export const initializeToken = async () => {
  return Promise.resolve();
};
